import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';
import { specials } from '@/utils/data';
import type { SpecialArticle } from '@/types';

export default function SpecialPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('id') || specials[0]?.id;
  const active = specials.find((s) => s.id === activeId) || specials[0];
  const [article, setArticle] = useState<SpecialArticle | null>(null);

  usePageMeta({
    title: active ? `${active.title} - 影视影评与攻略专题 - 御宅屋` : '影视影评与攻略专题 - 御宅屋',
    description: active
      ? active.summary
      : '御宅屋影评与攻略专题，汇集深度影评、影视解读与观影攻略。',
    keywords: '影视影评,观影攻略,影视专题,影评合集,御宅屋专题',
  });

  const selectSpecial = (id: string) => {
    setSearchParams({ id });
    setArticle(null);
  };

  return (
    <div className="w-full px-4 md:px-8 pb-16">
      <div className="pt-2">
        <Breadcrumb
          items={
            active
              ? [{ label: '影评与攻略专题', to: '/special' }, { label: active.title }]
              : [{ label: '影评与攻略专题' }]
          }
        />
      </div>

      <div className="rounded-lg bg-gradient-to-r from-primary-900/60 to-background-100 border border-background-200/70 p-6 md:p-8 mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          影视影评与攻略专题
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          我们围绕同一主题，把影评、解读与观影攻略整理成专题。从一部作品出发，读懂一种类型、一段创作，或是整个行业的变迁。
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {specials.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => selectSpecial(s.id)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
              s.id === active?.id
                ? 'bg-accent-500 text-background-50 border-accent-500 font-medium'
                : 'border-background-300/60 text-foreground-700 hover:border-accent-500/50 hover:text-accent-400'
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {active && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 min-w-0">
            <div className="rounded-lg overflow-hidden border border-background-200/70 bg-background-100">
              <div className="relative w-full h-56 md:h-80 overflow-hidden bg-background-200">
                <img
                  src={active.cover}
                  alt={active.title}
                  title={`${active.title} 影视专题`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6">
                  <span className="inline-block px-2.5 py-1 rounded-md bg-primary-500/90 text-foreground-950 text-xs mb-3">
                    专题
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    {active.title}
                  </h2>
                  <p className="mt-1 text-xs md:text-sm text-secondary-300">{active.subtitle}</p>
                </div>
              </div>
              <p className="p-6 text-sm md:text-base text-foreground-600 leading-[1.9]">
                {active.summary}
              </p>
            </div>

            <h3 className="mt-8 mb-4 font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              专题文章集锦（{active.articles.length} 篇）
            </h3>
            <div className="flex flex-col gap-4">
              {active.articles.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setArticle(a)}
                  className="group text-left flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-44 h-40 sm:h-[104px] shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={a.cover}
                      alt={a.title}
                      title={`${a.title} 专题文章`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-secondary-300">{a.source}</span>
                    <h4 className="mt-1 text-base font-medium text-foreground-950 line-clamp-1 group-hover:text-accent-400 transition-colors">
                      {a.title}
                    </h4>
                    <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{a.summary}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs text-accent-400">
                      阅读全文
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 sticky top-24">
              <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                更多专题
              </h3>
              <div className="flex flex-col gap-4">
                {specials.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => selectSpecial(s.id)}
                    className={`text-left group ${
                      s.id === active.id ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                    } transition-opacity cursor-pointer`}
                  >
                    <div className="w-full h-24 rounded-md overflow-hidden bg-background-200 mb-2">
                      <img
                        src={s.cover}
                        alt={s.title}
                        title={`${s.title} 影视专题`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <h4
                      className={`text-sm line-clamp-1 transition-colors ${
                        s.id === active.id
                          ? 'text-accent-400'
                          : 'text-foreground-900 group-hover:text-accent-400'
                      }`}
                    >
                      {s.title}
                    </h4>
                    <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{s.summary}</p>
                  </button>
                ))}
              </div>
              <Link
                to="/category/movie"
                className="mt-5 inline-flex items-center gap-1 text-sm text-accent-400 hover:underline"
              >
                去电影频道看看
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      )}

      {article && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-background-50/80 backdrop-blur-sm"
          onClick={() => setArticle(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg bg-background-100 border border-background-200/70 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-48 md:h-64 overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} 专题文章`}
                className="w-full h-full object-cover object-top"
              />
              <button
                type="button"
                onClick={() => setArticle(null)}
                aria-label="关闭"
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/70 text-foreground-900 hover:text-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 md:p-7">
              <span className="text-xs text-secondary-300">{article.source}</span>
              <h3 className="mt-2 font-heading text-xl font-bold text-foreground-950">
                {article.title}
              </h3>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-[2]">
                {article.summary}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-[2]">
                本文为御宅屋专题演示内容，旨在展示影评与攻略类文章的组织方式。完整内容以站内正式发布为准，此处仅作浏览体验演示。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/special"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回专题
                </Link>
                <button
                  type="button"
                  onClick={() => setArticle(null)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}